import React, { useEffect, useState } from "react";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";

import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

import { useNavigate } from "react-router-dom";
import { googleLogout } from "@react-oauth/google";

import {
  getCheckinEvents,
  checkinEvent,
  logoutUser,
  getCheckinHours,
} from "../../services/UserService";

import DrawerAppBar from "../../components/DrawerAppBar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ProgressBar from "../../components/ProgressBar";

import Title from "../../components/Title";
import dayjs from "dayjs";
import { hasError, getError } from "../../services/ServiceHelper";
import { showOKCancelDialog, showToast } from "../../components/ATPDialogs";

const CheckinWelcome = () => {
  const [_jwt] = useJwtStorage();
  const [_school] = useLocalStorage("Auth", "school", null);

  const [_showProgress, setShowProgress] = useState(false);
  const [_statusText, setStatusText] = React.useState("LOADING...");
  const [_buttonText, setButtonText] = React.useState("Check In/Out Here");
  const [_actionToDo, setActionToDo] = React.useState("I");
  const [_actionToDoCatId, setActionToDoCatId] = React.useState(0);
  const [_checkinId, setCheckinId] = React.useState(0);
  const [_checkoutId, setCheckoutId] = React.useState(0);

  const [_minimumHours, setMinimumHours] = React.useState("");
  const [_seatHours, setSeatHours] = React.useState("");
  const [_comment, setComment] = React.useState("");

  const navigate = useNavigate();

  useEffect(() => {
    initContent();
    initCheckinHours();
  }, []);

  const initCheckinHours = () => {
    setShowProgress(true);

    getCheckinHours(_school, _jwt)
      .then((results) => {
        setMinimumHours(results.data.minimum_hours + " hour(s)");

        const seat_hour = Math.floor(results.data.seat_hours);
        const seat_minutes = Math.floor(
          (results.data.seat_hours - seat_hour) * 60.0
        );

        setSeatHours(seat_hour + " hour(s), " + seat_minutes + " minute(s)");
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const initContent = () => {
    setShowProgress(true);

    const currentDate = dayjs().format("YYYY-MM-DD");
    getCheckinEvents(_school, _jwt, currentDate)
      .then((results) => {
        if (hasError(results) === true) {
          showToast(
            "E",
            "Unable to get Student Passes: ",
            getError(results)
          );
          return;
        }

        const events = results.data.events;
        const checkinId = results.data.checkin_id;
        const checkoutId = results.data.checkout_id;

        setCheckinId(checkinId);
        setCheckoutId(checkoutId);

        if (events.length === 0) {
          setStatusText("YOU HAVE NOT CHECKED IN TODAY.");
          setButtonText("CHECK IN");
          setActionToDo("I");
          setActionToDoCatId(checkinId);
        } else if (events[0].isCheckin === "Y") {
          const checkinDate = dayjs(events[0].dateCreated);
          setStatusText(
            "YOU LAST CHECKED IN AT " + checkinDate.format("hh:mm:ss a") + "."
          );
          setButtonText("CHECK OUT");
          setActionToDo("O");
          setActionToDoCatId(checkoutId);
        } else if (events[0].isCheckout === "Y") {
          // checked out
          const checkoutDate = dayjs(events[0].dateCreated);
          const checkinDate = dayjs(events[1].dateCreated);
          setStatusText(
            `YOU LAST CHECKED IN AT ${checkinDate.format(
              "hh:mm:ss a"
            )}, then CHECKED OUT AT ${checkoutDate.format("hh:mm:ss a")}.`
          );
          setButtonText("CHECK BACK IN");
          setActionToDo("I");
          setActionToDoCatId(checkinId);
        }
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const handleCheckin = () => {
    const currentDate = dayjs().format("YYYY-MM-DD");

    setShowProgress(true);
    checkinEvent(_school, _jwt, currentDate, _actionToDoCatId, _comment)
      .then((results) => {
        // switch to do
        if (_actionToDo === "I") {
          showOKCancelDialog(
            "Check In Complete!",
            "Click OK to log out, or Cancel to view reports.",
            () => {
              logoutUser();
              googleLogout();
              navigate("/checkin");
              return;
            }
          );

          // not logging out?  then next action is a checkout
          setActionToDo("O");
          setActionToDoCatId(_checkoutId);
        } else {
          // check out
          showOKCancelDialog(
            "Check Out Complete!",
            "Click OK to log out, or Cancel to view reports.",
            () => {
              logoutUser();
              googleLogout();
              navigate("/checkin");
              return;
            }
          );
          setActionToDo("I");
          setActionToDoCatId(_checkinId);
        }

        initContent();
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const handleChangeComment = (event) => {
    setComment(event.target.value);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <ProgressBar showProgress={_showProgress} />

        <Grid container spacing={2} sx={{ mt: 0.0 }}>
          <Grid item xs={6}>
            <Title text={"Your Status:"} />
            <Alert severity="warning" sx={{ mt: 1 }}>
              <AlertTitle>CHECK IN STATUS</AlertTitle>
              {_statusText}
            </Alert>

            <Grid container spacing={1} sx={{ mt: 1.0 }}>
              <Grid item xs={9}>
                <TextField
                  type="text"
                  variant="outlined"
                  fullWidth
                  size="small"
                  onChange={handleChangeComment}
                  placeholder="Enter Optional Comment"
                  value={_comment}
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  sx={{ mt: 0 }}
                  variant="contained"
                  onClick={handleCheckin}
                  disabled={_checkinId === 0 || _checkoutId === 0}
                >
                  {_buttonText}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Title text={"Time This Week:"} />
            <Grid container spacing={2} sx={{ mt: 0.0 }}>
              <Grid item xs={6}>
                Minimum Hours:
              </Grid>
              <Grid item xs={6}>
                {_minimumHours}
              </Grid>
              <Grid item xs={6}>
                Seat Hours:
              </Grid>
              <Grid item xs={6}>
                {_seatHours}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default CheckinWelcome;
